import { apiSlice } from "./apiSlice";
import config from "../../config.json";

// Returns parsed JSON when the body is valid JSON (objects/arrays),
// otherwise the raw text ("success" / "fail" / "") — mirrors the
// behaviour the components previously relied on from axios.
const smartResponse = async (response) => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (credentials) => ({
        url: config.AuthPassword || "Auth/password",
        method: "POST",
        body: credentials,
      }),
    }),
    validateUser: builder.mutation({
      query: (data) => ({
        url: config.ValidateUser,
        method: "POST",
        body: data,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.token) {
            localStorage.setItem("token", data.token);
          }
        } catch (err) {}
      },
    }),
    refreshToken: builder.mutation({
      query: (refreshToken) => ({
        url: config.AuthRefresh || "Auth/refresh",
        method: "POST",
        body: { refreshToken },
      }),
    }),
    getUserDetails: builder.query({
      query: (id) => `${config.GetUser}${id}`,
      providesTags: ["User"],
    }),
    getPartnerUserDetails: builder.query({
      query: (id) => `${config.GetUser}${id}`,
      providesTags: ["User"],
    }),
    updateLoginLogoutStatus: builder.mutation({
      query: (refreshToken) => ({
        url: config.AuthLogout || "Auth/logout",
        method: "POST",
        body: { refreshToken },
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {}
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("userType");
      },
    }),
    addHostDetails: builder.mutation({
      query: (data) => ({
        url: config.AddHostDetails,
        method: "POST",
        body: data,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data) {
            localStorage.setItem("hostId", data);
          }
        } catch (err) {}
      },
    }),
    getUserProfilePic: builder.query({
      query: (id) => ({
        url: `${config.GetProfilePicData}${id}`,
        responseHandler: "text",
      }),
      providesTags: ["User"],
    }),
    getUserProfileLogo: builder.query({
      query: (id) => ({
        url: `${config.GetCompanyLogData}${id}`,
        responseHandler: "text",
      }),
      providesTags: ["User"],
    }),
    updateProfileData: builder.mutation({
      query: (data) => ({
        url: config.UpdateProfileData,
        method: "POST",
        body: data,
        responseHandler: "text",
      }),
      invalidatesTags: ["User"],
    }),
    uploadProfilePic: builder.mutation({
      query: (data) => ({
        url: config.UploadProfilePic,
        method: "POST",
        body: data,
        responseHandler: "text",
      }),
      invalidatesTags: ["User"],
    }),
    uploadCompanyLogo: builder.mutation({
      query: (data) => ({
        url: config.UploadCompanyLogo,
        method: "POST",
        body: data,
        responseHandler: "text",
      }),
      invalidatesTags: ["User"],
    }),
    updateLoginStatus: builder.query({
      query: (id) => `${config.UpdateLoginStatus}${id}`,
    }),
    sendSecurityCodeToEmail: builder.query({
      query: ({ username, email, type }) => ({
        url: `${config.SendSecurityCodeToEmail}${username}&email=${email}${
          type ? `&type=${type}` : ""
        }`,
        responseHandler: "text",
      }),
    }),
    sendSecurityCodeToPhone: builder.query({
      query: ({ username, phone, type }) => ({
        url: `${config.SendSecurityCodeToPhone}${username}&phone=${encodeURIComponent(
          phone
        )}${type ? `&type=${type}` : ""}`,
        responseHandler: "text",
      }),
    }),
    verifySecurityCode: builder.query({
      query: ({ email, code, type = "identity" }) => ({
        url: `${config.VerifySecurityCode}${email}&code=${code}&type=${type}`,
        responseHandler: "text",
      }),
    }),
    verifyUserByUserName: builder.mutation({
      query: (data) => ({
        url: config.VerifyUserByUserName,
        method: "POST",
        body: data,
        responseHandler: smartResponse,
      }),
    }),
    verifyUserByEmail: builder.mutation({
      query: (data) => ({
        url: config.VerifyUserByEmail,
        method: "POST",
        body: data,
        responseHandler: smartResponse,
      }),
    }),
    verifySecQuestion: builder.mutation({
      query: (data) => ({
        url: config.VerifySecQuestion,
        method: "POST",
        body: data,
        responseHandler: smartResponse,
      }),
    }),
    contactAdmin: builder.mutation({
      query: (data) => ({
        url: config.ContactAdmin,
        method: "POST",
        body: data,
        responseHandler: smartResponse,
      }),
    }),
    resetPassword: builder.mutation({
      query: (data) => ({
        url: config.ResetPassword,
        method: "POST",
        body: data,
        responseHandler: smartResponse,
      }),
    }),
    authStart: builder.mutation({
      query: (data) => ({
        url: config.AuthStart || "Auth/start",
        method: "POST",
        body: data,
        responseHandler: smartResponse,
      }),
    }),
    getUserType: builder.query({
      query: (validId) => ({
        url: `${config.GetUserType}${validId}`,
        responseHandler: "text",
      }),
    }),
    getClientDetails: builder.query({
      query: () => config.GetClientDetails,
    }),
  }),
});

export const {
  useLoginMutation,
  useValidateUserMutation,
  useRefreshTokenMutation,
  useGetUserDetailsQuery,
  useLazyGetUserDetailsQuery,
  useGetPartnerUserDetailsQuery,
  useLazyGetPartnerUserDetailsQuery,
  useUpdateLoginLogoutStatusMutation,
  useAddHostDetailsMutation,
  useGetUserProfilePicQuery,
  useLazyGetUserProfilePicQuery,
  useGetUserProfileLogoQuery,
  useLazyGetUserProfileLogoQuery,
  useUpdateProfileDataMutation,
  useUploadProfilePicMutation,
  useUploadCompanyLogoMutation,
  useLazyUpdateLoginStatusQuery,
  useLazySendSecurityCodeToEmailQuery,
  useLazySendSecurityCodeToPhoneQuery,
  useLazyVerifySecurityCodeQuery,
  useVerifyUserByUserNameMutation,
  useVerifyUserByEmailMutation,
  useVerifySecQuestionMutation,
  useContactAdminMutation,
  useResetPasswordMutation,
  useAuthStartMutation,
  useLazyGetUserTypeQuery,
  useLazyGetClientDetailsQuery,
} = authApiSlice;
